// Vendors
import React, { useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, FieldError, SubmitHandler, useForm } from 'react-hook-form'

// Functions
import { useCreateRecord } from 'hooks/useCreateRecord'

// Components
import { FieldText } from 'components/FieldText'
import { FieldMask } from 'components/FieldMask'
import { FieldDate } from 'components/FieldDate'
import { FormFooter } from 'components/FormFooter'
import { FieldRadioButton } from 'components/FieldRadioButton'

// Schemas
import { schema } from '../schema'

// Styles
import { Flex, Grid, GridItem, useToast } from '@chakra-ui/react'

// Types
import { InfluencerNewForm } from '../types'
import { FieldToggle } from 'components/FieldToggle'

type TabNewInfluencerProps = {
  endpoint: string
}

export const TabInfluencerNew = (props: TabNewInfluencerProps) => {
  /*
  |-----------------------------------------------------------------------------
  | Constants
  |-----------------------------------------------------------------------------
  |
  |
  */

  const { endpoint } = props

  const { t } = useTranslation('influencer')
  const history = useHistory()

  const { create } = useCreateRecord({ endpoint })
  const toast = useToast({ position: 'top-right' })

  const {
    control,
    setError,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<InfluencerNewForm>({
    resolver: yupResolver(schema(t)),
  })

  /*
  |-----------------------------------------------------------------------------
  | Memos
  |-----------------------------------------------------------------------------
  |
  |
  */

  const genresOptions = useMemo(
    () => [
      {
        label: t('form.gender.options.male'),
        value: 'M',
      },
      {
        label: t('form.gender.options.female'),
        value: 'F',
      },
    ],
    [t]
  )

  /*
  |-----------------------------------------------------------------------------
  | Functions
  |-----------------------------------------------------------------------------
  |
  |
  */

  const onSubmit: SubmitHandler<InfluencerNewForm> = React.useCallback(
    async (data) => {
      try {
        await create(data)

        toast({
          title: t('toast.success.title', {
            type: t('common:toast.success.create'),
          }),
          status: 'success',
        })

        history.push('/influencers')
      } catch (error) {
        if (error && error instanceof Array) {
          error.forEach((fieldError) => {
            setError(fieldError.field, { message: fieldError.message })
          })
        }
      }
    },
    [create, history, setError, t, toast]
  )

  /*
  |-----------------------------------------------------------------------------
  | Renders
  |-----------------------------------------------------------------------------
  |
  |
  */

  return (
    <Flex
      as="form"
      h="full"
      flexDirection="column"
      justifyContent="space-between"
      onSubmit={handleSubmit(onSubmit)}
      position="relative"
    >
      <Controller
        name="status"
        control={control}
        defaultValue={true}
        render={({ field: { onChange, value, ref, name } }) => (
          <FieldToggle
            name={name}
            position="absolute"
            top={-16}
            right={0}
            ref={ref}
            isChecked={value}
            onChange={onChange}
            error={errors.status}
          />
        )}
      />
      <Grid
        my="4"
        gap="10"
        templateColumns={{ base: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }}
      >
        <GridItem colSpan={2}>
          <FieldText
            label={t('form.name.label')}
            error={errors.name}
            isRequired
            {...register('name')}
          />
        </GridItem>

        <GridItem colSpan={2}>
          <FieldText
            label={t('form.email.label')}
            error={errors.email}
            isRequired
            {...register('email')}
          />
        </GridItem>

        <GridItem colSpan={2}>
          <FieldMask
            mask="+55 (99) 9 9999-9999"
            label={t('form.phoneNumber.label')}
            error={errors.phoneNumber}
            isRequired
            {...register('phoneNumber')}
          />
        </GridItem>

        <GridItem colSpan={2}>
          <FieldRadioButton
            label={t('form.gender.label')}
            name="gender"
            control={control}
            options={genresOptions}
            radioButtonDirection="row"
            isRequired
          />
        </GridItem>

        <GridItem colSpan={2}>
          <Controller
            name="birthdate"
            control={control}
            render={({ field }) => (
              <FieldDate
                dateFormat="dd/MM/yyyy"
                label={t('form.birthdate.label')}
                maxDate={new Date()}
                peekNextMonth
                showYearDropdown
                showMonthDropdown
                name={field.name}
                dropdownMode="select"
                selected={field.value}
                onChange={field.onChange}
                error={errors.birthdate as FieldError}
                isRequired
              />
            )}
          />
        </GridItem>
      </Grid>

      <FormFooter isSubmitting={isSubmitting} />
    </Flex>
  )
}
